import { z } from 'zod'

/**
 * Request shape for attaching taxonomy tags to a taggable content item
 * (listing, event, etc).
 *
 * - `mainCategoryId` — the top-level category (required)
 * - `subCategoryTags` — at least one required; narrows down the main category
 * - `subSubCategoryTags` — optional further refinement (can be empty)
 * - `amenityIds` — optional amenity tags (can be empty)
 */
export const taxonomyTagsSchema = z.strictObject({
  mainCategoryId: z.string().uuid(),
  subCategoryTags: z.array(z.string().uuid()).min(1, 'At least one sub-category is required'),
  subSubCategoryTags: z.array(z.string().uuid()),
  amenityIds: z.array(z.string().uuid()),
})

export type TaxonomyTagsInput = z.infer<typeof taxonomyTagsSchema>

/**
 * Flattens a taxonomy tags object into a single deduplicated array of tag UUIDs
 * (preserving order: main → sub → sub-sub → amenities).
 */
export function flattenTaxonomyTagIds(tagsInput: TaxonomyTagsInput): string[] {
  return [
    ...new Set([
      tagsInput.mainCategoryId,
      ...tagsInput.subCategoryTags,
      ...tagsInput.subSubCategoryTags,
      ...tagsInput.amenityIds,
    ]),
  ]
}
